import { useState } from "react";
import { MapPin, Phone, Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/constants";
import { submitContact } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Message sent!", {
        description: "Our team will get back to you shortly.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="py-14 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
              Contact
            </p>
            <h1 className="mt-4 font-display text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
              Let&apos;s <span className="text-[#28C0D8]">talk.</span>
            </h1>
            <p className="mt-6 text-[17px] text-[#94A3B8] max-w-2xl">
              Questions about our launch, delivery, open roles, or anything
              else — our Bethesda team is already here and ready to help.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-4 md:gap-8">
            <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
              <div className="rounded-2xl border border-white/10 bg-[#13161C] p-7">
                <div className="flex items-center gap-3 mb-3">
                  <Phone className="h-5 w-5 text-[#28C0D8]" />
                  <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                    Call
                  </p>
                </div>
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  className="font-display text-2xl font-semibold text-white hover:text-[#28C0D8] transition"
                >
                  {BRAND.phone}
                </a>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#13161C] p-7">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="h-5 w-5 text-[#28C0D8]" />
                  <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                    Email
                  </p>
                </div>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="font-display text-xl font-semibold text-white hover:text-[#28C0D8] transition break-all"
                >
                  {BRAND.email}
                </a>
                <p className="mt-4 text-sm text-[#94A3B8]">
                  Careers:{" "}
                  <a
                    href={`mailto:${BRAND.careersEmail}`}
                    className="text-[#28C0D8] hover:underline break-all"
                  >
                    {BRAND.careersEmail}
                  </a>
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#13161C] p-7">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className="h-5 w-5 text-[#28C0D8]" />
                  <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                    Visit
                  </p>
                </div>
                <p className="font-display text-lg font-semibold text-white leading-snug">
                  {BRAND.address.line1}
                  <br />
                  {BRAND.address.line2}
                </p>
              </div>

              <div className="rounded-2xl overflow-hidden border border-white/10 h-[240px]">
                <iframe
                  title="MD Supply Wellness map"
                  src={BRAND.mapEmbed}
                  width="100%"
                  height="100%"
                  style={{
                    border: 0,
                    filter: "invert(0.92) hue-rotate(180deg) saturate(0.6)",
                  }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            <div className="col-span-12 lg:col-span-7">
              <div className="rounded-3xl border border-white/10 bg-[#13161C] p-8 md:p-10">
                <h2 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  Send us a message
                </h2>
                <p className="mt-2 text-sm text-[#94A3B8]">
                  We typically reply within one business day.
                </p>

                {success ? (
                  <div
                    className="mt-8 flex items-start gap-4 rounded-xl border border-[#28C0D8]/40 bg-[#28C0D8]/10 p-5"
                    data-testid="contact-success"
                  >
                    <CheckCircle2 className="h-6 w-6 text-[#28C0D8] shrink-0" />
                    <div>
                      <p className="font-display text-lg font-semibold text-white">
                        Thanks — your message is on its way.
                      </p>
                      <p className="mt-1 text-sm text-white/80">
                        Our team will get back to you shortly. Need immediate
                        help? Call us at{" "}
                        <a
                          href={`tel:${BRAND.phoneRaw}`}
                          className="text-[#28C0D8] hover:underline"
                        >
                          {BRAND.phone}
                        </a>
                        .
                      </p>
                      <button
                        type="button"
                        onClick={() => setSuccess(false)}
                        className="mt-4 text-sm text-[#28C0D8] hover:underline"
                      >
                        Send another message
                      </button>
                    </div>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="mt-8 space-y-5"
                    data-testid="contact-form"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <label className="block">
                        <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                          Full Name
                        </span>
                        <input
                          type="text"
                          required
                          value={form.name}
                          onChange={update("name")}
                          placeholder="Jane Doe"
                          className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30"
                          data-testid="contact-name-input"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                          Email
                        </span>
                        <input
                          type="email"
                          required
                          value={form.email}
                          onChange={update("email")}
                          placeholder="you@email.com"
                          className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30"
                          data-testid="contact-email-input"
                        />
                      </label>
                    </div>
                    <label className="block">
                      <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                        Phone <span className="lowercase text-[#64748B]">(optional)</span>
                      </span>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="(301) 555-0100"
                        className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30"
                        data-testid="contact-phone-input"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                        Message
                      </span>
                      <textarea
                        required
                        value={form.message}
                        onChange={update("message")}
                        rows={5}
                        placeholder="How can we help?"
                        className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30 resize-y"
                        data-testid="contact-message-input"
                      />
                    </label>

                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <p className="text-xs text-[#64748B] max-w-xs">
                        By submitting, you agree to be contacted by MD Supply
                        Wellness about your inquiry.
                      </p>
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-teal-glow inline-flex items-center gap-2 rounded-full bg-[#28C0D8] px-5 py-2.5 text-sm md:px-6 md:py-3 md:text-[15px] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC] disabled:opacity-60"
                        data-testid="contact-submit-btn"
                      >
                        {loading ? "Sending…" : "Send message"}
                        {!loading && <Send className="h-4 w-4" />}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
